import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Plane, MapPin, Calendar, DollarSign, Phone, Mail, CheckCircle, Clock, XCircle, Filter, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPilots = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  useAdminAuth(); // Protect this route
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [approvedPilots, setApprovedPilots] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  const [selectedPilotEmail, setSelectedPilotEmail] = useState('');
  const [completingId, setCompletingId] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');

  useEffect(() => {
    fetchBookings();
    fetchApprovedPilots();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, filter]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/pilot-bookings`);
      setBookings(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load pilot bookings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    if (filter === 'all') {
      setFilteredBookings(bookings);
    } else {
      setFilteredBookings(bookings.filter(b => b.status === filter));
    }
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/pilot-bookings/${bookingId}/status`, { status: newStatus });
      toast({ title: 'Success', description: `Booking status updated to ${newStatus}` });
      fetchBookings();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update booking status', variant: 'destructive' });
    }
  };

  const fetchApprovedPilots = async () => {
    try {
      const res = await axios.get(`${API}/pilot-registrations/approved`);
      setApprovedPilots(res.data);
    } catch { /* no approved pilots yet */ }
  };

  const handleAssignPilot = async (bookingId) => {
    if (!selectedPilotEmail) { toast({ title: 'Select a pilot', variant: 'destructive' }); return; }
    try {
      await axios.put(`${API}/pilot-bookings/${bookingId}/assign`, { pilot_email: selectedPilotEmail });
      toast({ title: 'Pilot Assigned', description: 'Pilot has been notified via email' });
      setAssigningId(null);
      setSelectedPilotEmail('');
      fetchBookings();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to assign', variant: 'destructive' });
    }
  };

  const handleMarkCompleted = async (bookingId) => {
    try {
      await axios.put(`${API}/pilot-bookings/${bookingId}/mark-completed`, {
        payment_amount: paymentAmount ? parseFloat(paymentAmount) : null
      });
      toast({ title: 'Completed', description: 'Booking marked as completed' });
      setCompletingId(null);
      setPaymentAmount('');
      fetchBookings();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to complete', variant: 'destructive' });
    }
  };

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    assigned: bookings.filter(b => b.status === 'assigned').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Loading pilot bookings...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1e3a5f]">Pilot Bookings Management</h1>
              <p className="text-gray-600 mt-2">Manage pilot service requests</p>
            </div>
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              className="border-gray-300"
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Bookings</p>
                  <p className="text-3xl font-bold text-[#1e3a5f]">{stats.total}</p>
                </div>
                <Plane className="w-10 h-10 text-blue-600" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-600" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Confirmed</p>
                  <p className="text-3xl font-bold text-green-600">{stats.confirmed}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Assigned</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.assigned}</p>
                </div>
                <UserPlus className="w-10 h-10 text-blue-600" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.completed}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-purple-600" />
              </div>
            </Card>
          </div>

          {/* Filter */}
          <Card className="p-6 mb-6">
            <Label>Filter by Status</Label>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                size="sm"
              >
                All Bookings
              </Button>
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilter('pending')}
                size="sm"
              >
                Pending
              </Button>
              <Button
                variant={filter === 'confirmed' ? 'default' : 'outline'}
                onClick={() => setFilter('confirmed')}
                size="sm"
              >
                Confirmed
              </Button>
              <Button
                variant={filter === 'assigned' ? 'default' : 'outline'}
                onClick={() => setFilter('assigned')}
                size="sm"
              >
                Assigned
              </Button>
              <Button
                variant={filter === 'completed' ? 'default' : 'outline'}
                onClick={() => setFilter('completed')}
                size="sm"
              >
                Completed
              </Button>
              <Button
                variant={filter === 'cancelled' ? 'default' : 'outline'}
                onClick={() => setFilter('cancelled')}
                size="sm"
              >
                Cancelled
              </Button>
            </div>
          </Card>

          {/* Bookings List */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">
              {filter === 'all' ? 'All Bookings' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Bookings`} ({filteredBookings.length})
            </h2>

            {filteredBookings.length === 0 ? (
              <Card className="p-12 text-center">
                <Plane className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No pilot bookings found</p>
              </Card>
            ) : (
              filteredBookings.map((booking) => (
                <Card key={booking.id} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          booking.status === 'completed' ? 'bg-purple-100 text-purple-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {booking.status}
                        </span>

                        {booking.pricing_option && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                            {booking.pricing_option === 'per_km' ? 'Per KM' : 'Per Hour'}
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-bold text-gray-900 mb-3">{booking.name}</h3>

                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-600">Pickup</p>
                            <p className="text-sm font-medium">{booking.pickup_location}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-red-600 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-600">Dropoff</p>
                            <p className="text-sm font-medium">{booking.dropoff_location}</p>
                          </div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 mb-3">
                        <div className="flex items-start gap-2">
                          <Mail className="w-4 h-4 text-gray-500 mt-1" />
                          <div>
                            <p className="text-xs text-gray-600">Email</p>
                            <p className="text-sm font-medium">{booking.email}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <Phone className="w-4 h-4 text-gray-500 mt-1" />
                          <div>
                            <p className="text-xs text-gray-600">Phone</p>
                            <p className="text-sm font-medium">{booking.phone}</p>
                          </div>
                        </div>

                        {booking.travel_date && (
                          <div className="flex items-start gap-2">
                            <Calendar className="w-4 h-4 text-gray-500 mt-1" />
                            <div>
                              <p className="text-xs text-gray-600">Travel Date</p>
                              <p className="text-sm font-medium">{new Date(booking.travel_date).toLocaleDateString()}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {booking.load_description && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-600">Load Description</p>
                          <p className="text-sm text-gray-700">{booking.load_description}</p>
                        </div>
                      )}

                      {booking.estimated_cost && (
                        <div className="flex items-center gap-2 text-green-700 font-semibold">
                          <DollarSign className="w-5 h-5" />
                          <span>Estimated Cost: {booking.estimated_cost}</span>
                        </div>
                      )}

                      <p className="text-xs text-gray-500 mt-3">
                        Booking ID: {booking.id} | Created: {new Date(booking.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      {booking.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleUpdateStatus(booking.id, 'confirmed')}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateStatus(booking.id, 'cancelled')}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            Cancel
                          </Button>
                        </>
                      )}

                      {(booking.status === 'pending' || booking.status === 'confirmed') && !booking.assigned_pilot_email && (
                        assigningId === booking.id ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 min-w-[200px]">
                            <p className="text-xs font-semibold text-blue-800 mb-2">Assign Pilot</p>
                            <select value={selectedPilotEmail} onChange={(e) => setSelectedPilotEmail(e.target.value)} className="w-full text-sm border rounded px-2 py-1 mb-2">
                              <option value="">Select pilot...</option>
                              {approvedPilots.map(p => <option key={p.email} value={p.email}>{p.name} — {p.company_name || p.email}</option>)}
                            </select>
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => handleAssignPilot(booking.id)} className="bg-blue-600 text-white text-xs flex-1">Assign</Button>
                              <Button size="sm" variant="outline" onClick={() => setAssigningId(null)} className="text-xs">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => { setAssigningId(booking.id); setSelectedPilotEmail(''); }} className="text-blue-600 border-blue-300">
                            <UserPlus className="w-3.5 h-3.5 mr-1" /> Assign Pilot
                          </Button>
                        )
                      )}

                      {booking.assigned_pilot_email && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2 min-w-[160px] text-xs">
                          <p className="text-green-800 font-semibold">Assigned to:</p>
                          <p className="text-green-700">{booking.assigned_pilot_name}</p>
                          <p className="text-green-600">{booking.assigned_pilot_phone}</p>
                        </div>
                      )}

                      {(booking.status === 'confirmed' || booking.status === 'assigned') && (
                        completingId === booking.id ? (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 min-w-[200px]">
                            <p className="text-xs font-semibold text-purple-800 mb-2">Complete & Record Payment</p>
                            <Input type="number" placeholder="Payment amount $" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} className="text-sm mb-2" />
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => handleMarkCompleted(booking.id)} className="bg-purple-600 text-white text-xs flex-1">Complete</Button>
                              <Button size="sm" variant="outline" onClick={() => setCompletingId(null)} className="text-xs">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" onClick={() => { setCompletingId(booking.id); setPaymentAmount(''); }} className="bg-purple-600 hover:bg-purple-700 text-white">
                            Mark Complete
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AdminPilots;
